import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  FaDatabase,
  FaCalendarDay,
  FaCalendarAlt,
  FaCalendarWeek,
  FaUserMd,
  FaSignOutAlt,
  FaSync,
  FaShieldAlt,
  FaUser,
  FaDownload,
  FaFileDownload,
  FaHistory,
} from "react-icons/fa";
import { buildApiUrl, API_ENDPOINTS } from "../../utils/apiConfig";
import { isAppointmentToday } from "../../utils/dateUtils";
import AddPatientForm from "../../components/AddPatientForm";
import "./Admin.css";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [stats, setStats] = useState({
    totalAppointments: 0,
    todayAppointments: 0,
    totalPatients: 0,
  });
  const [showAddPatientForm, setShowAddPatientForm] = useState(false);

  // Backup state
  const [backupStats, setBackupStats] = useState({
    recordCounts: {},
    totalRecords: 0,
    databaseSizeMB: 0,
    lastBackup: null,
  });
  const [backupLoading, setBackupLoading] = useState(false);
  const [backupMessage, setBackupMessage] = useState("");

  useEffect(() => {
    // Check if user is already authenticated
    const authStatus = localStorage.getItem("adminAuthenticated");
    if (authStatus === "true") {
      setIsAuthenticated(true);
      fetchStats();
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchStats();
      fetchBackupStats();
      // Set up auto-refresh every 3 minutes
      const interval = setInterval(() => {
        fetchStats();
        fetchBackupStats();
      }, 180000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);
    setLoginError("");

    try {
      const response = await fetch(buildApiUrl(API_ENDPOINTS.ADMIN.LOGIN), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (data.success) {
        setIsAuthenticated(true);
        localStorage.setItem("adminAuthenticated", "true");
        setUsername("");
        setPassword("");
        fetchStats();
      } else {
        setLoginError(data.message || "Invalid credentials");
      }
    } catch (error) {
      setLoginError("Login failed. Please try again.");
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem("adminAuthenticated");
    setStats({
      totalAppointments: 0,
      todayAppointments: 0,
      totalPatients: 0,
    });
  };

  const fetchStats = async () => {
    try {
      // Fetch appointments
      const appointmentsResponse = await fetch(
        buildApiUrl(API_ENDPOINTS.APPOINTMENTS.DATA)
      );
      const appointmentsData = await appointmentsResponse.json();

      // Fetch patients
      const patientsResponse = await fetch(
        buildApiUrl(API_ENDPOINTS.ADMIN.PATIENTS)
      );
      const patientsData = await patientsResponse.json();

      if (appointmentsData.success && patientsData.success) {
        const appointments = appointmentsData.appointments || [];
        const patients = patientsData.patients || [];

        // Calculate today's appointments using utility function
        const todayAppointments = appointments.filter((appointment) => {
          // Handle both old CSV format and new PostgreSQL format
          const appointmentDate =
            appointment["Preferred Date"] || appointment.preferred_date;

          // Use utility function to check if appointment is for today in IST
          return isAppointmentToday(appointmentDate);
        });

        setStats({
          totalAppointments: appointments.length,
          todayAppointments: todayAppointments.length,
          totalPatients: patients.length,
        });
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const navigateToSection = (section) => {
    navigate(`/admin/${section}`);
  };

  // ==================== BACKUP FUNCTIONS ====================

  const fetchBackupStats = async () => {
    try {
      const response = await fetch(buildApiUrl("/api/admin/backup/stats"));
      const data = await response.json();

      if (data.success) {
        setBackupStats(data.stats);
      }
    } catch (error) {
      console.error("Error fetching backup stats:", error);
    }
  };

  const createBackup = async () => {
    setBackupLoading(true);
    setBackupMessage("");

    try {
      const response = await fetch(buildApiUrl("/api/admin/backup/create"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();

      if (data.success) {
        setBackupMessage(
          `✅ Backup created successfully! ${data.summary.totalRecords} records exported.`
        );
        // Refresh backup stats
        fetchBackupStats();
      } else {
        setBackupMessage(`❌ Error: ${data.message}`);
      }
    } catch (error) {
      console.error("Error creating backup:", error);
      setBackupMessage("❌ Error creating backup. Please try again.");
    } finally {
      setBackupLoading(false);
    }
  };

  const downloadFullBackup = async () => {
    try {
      const response = await fetch(buildApiUrl("/api/admin/backup/download"));

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `clinic_backup_${
          new Date().toISOString().split("T")[0]
        }.zip`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        setBackupMessage("✅ Full backup downloaded successfully!");
      } else {
        const errorData = await response.json();
        setBackupMessage(`❌ Error: ${errorData.message}`);
      }
    } catch (error) {
      console.error("Error downloading backup:", error);
      setBackupMessage("❌ Error downloading backup. Please try again.");
    }
  };

  const downloadTableCSV = async (tableName) => {
    try {
      const response = await fetch(
        buildApiUrl(`/api/admin/backup/table/${tableName}`)
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${tableName}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        setBackupMessage(`✅ ${tableName} table downloaded successfully!`);
      } else {
        const errorData = await response.json();
        setBackupMessage(`❌ Error: ${errorData.message}`);
      }
    } catch (error) {
      console.error("Error downloading table:", error);
      setBackupMessage("❌ Error downloading table. Please try again.");
    }
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="admin-login-page">
        <motion.div
          className="login-container"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="login-header">
            <div className="login-logo">
              <FaShieldAlt />
            </div>
            <h1>Admin Login</h1>
            <p>SATYA HEART CLINIC Administration</p>
          </div>

          <form onSubmit={handleLogin} className="login-form">
            <div className="form-group">
              <label htmlFor="username">
                <FaDatabase /> Username
              </label>
              <input
                type="text"
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                placeholder="Enter username"
                disabled={isLoggingIn}
                autoComplete="username"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">
                <FaShieldAlt /> Password
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Enter password"
                disabled={isLoggingIn}
                autoComplete="current-password"
              />
            </div>

            {loginError && <div className="login-error">{loginError}</div>}

            <button type="submit" className="login-btn" disabled={isLoggingIn}>
              {isLoggingIn ? (
                <>
                  <div className="spinner"></div>
                  Signing In...
                </>
              ) : (
                <>
                  <FaDatabase />
                  Sign In
                </>
              )}
            </button>
          </form>

          <div className="login-footer">
            <p>Restricted Access - Authorized Personnel Only</p>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <motion.div
      className="admin-page"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="admin-header">
        <div className="header-content">
          <div className="header-left">
            <h1>
              <FaDatabase /> Clinic Administration Dashboard
            </h1>
            <p>Manage appointments and patient information</p>
          </div>
          <div className="header-right">
            <button
              className="refresh-btn"
              onClick={fetchStats}
              title="Refresh statistics"
            >
              <FaSync /> Refresh Stats
            </button>
            <button className="logout-btn" onClick={handleLogout}>
              <FaSignOutAlt /> Logout
            </button>
          </div>
        </div>
      </div>

      {/* Statistics Overview */}
      <div className="stats-overview">
        <div className="stat-card">
          <div className="stat-icon">
            <FaCalendarWeek />
          </div>
          <div className="stat-content">
            <h3>Total Appointments</h3>
            <p className="stat-number">{stats.totalAppointments}</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <FaCalendarDay />
          </div>
          <div className="stat-content">
            <h3>Today's Appointments</h3>
            <p className="stat-number">{stats.todayAppointments}</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">
            <FaUserMd />
          </div>
          <div className="stat-content">
            <h3>Total Patients</h3>
            <p className="stat-number">{stats.totalPatients}</p>
          </div>
        </div>
      </div>

      {/* Navigation Cards */}
      <div className="admin-navigation">
        <h2>Quick Access</h2>
        <div className="nav-cards">
          <motion.div
            className="nav-card"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigateToSection("today-appointment")}
          >
            <div className="nav-card-icon">
              <FaCalendarDay />
            </div>
            <h3>Today's Appointments</h3>
            <p>View and manage today's scheduled appointments</p>
            <div className="nav-card-count">
              {stats.todayAppointments} appointment
              {stats.todayAppointments !== 1 ? "s" : ""}
            </div>
          </motion.div>

          <motion.div
            className="nav-card"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigateToSection("select-date")}
          >
            <div className="nav-card-icon">
              <FaCalendarAlt />
            </div>
            <h3>Select Date</h3>
            <p>View appointments for a specific date</p>
          </motion.div>

          <motion.div
            className="nav-card"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigateToSection("all-appointments")}
          >
            <div className="nav-card-icon">
              <FaCalendarWeek />
            </div>
            <h3>All Appointments</h3>
            <p>View and manage all appointment bookings</p>
            <div className="nav-card-count">
              {stats.totalAppointments} total
            </div>
          </motion.div>

          <motion.div
            className="nav-card"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigateToSection("patients-database")}
          >
            <div className="nav-card-icon">
              <FaUserMd />
            </div>
            <h3>Patients Database</h3>
            <p>View and manage patient information</p>
            <div className="nav-card-count">{stats.totalPatients} patients</div>
          </motion.div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <h2>Quick Actions</h2>
        <div className="action-buttons">
          <button
            className="action-btn"
            onClick={() => navigateToSection("today-appointment")}
          >
            <FaCalendarDay /> View Today
          </button>
          <button
            className="action-btn"
            onClick={() => navigateToSection("patients-database")}
          >
            <FaUserMd /> Search Patients
          </button>
          <button
            className="action-btn"
            onClick={() => setShowAddPatientForm(true)}
          >
            <FaUser /> Add New Patient
          </button>
          <button
            className="action-btn"
            onClick={() => navigateToSection("all-appointments")}
          >
            <FaCalendarWeek /> All Bookings
          </button>
        </div>
      </div>

      {/* Data Backup Section */}
      <div className="backup-section">
        <div className="section-header">
          <h2>
            <FaDatabase /> Data Backup & Export
          </h2>
          <p>
            Create backups of your clinic data in CSV format for safety and
            sharing
          </p>
        </div>

        {/* Backup Statistics */}
        <div className="backup-stats">
          <div className="backup-stat-card">
            <div className="backup-stat-icon">
              <FaDatabase />
            </div>
            <div className="backup-stat-content">
              <h4>Total Records</h4>
              <p className="backup-stat-number">{backupStats.totalRecords}</p>
            </div>
          </div>

          <div className="backup-stat-card">
            <div className="backup-stat-icon">
              <FaFileDownload />
            </div>
            <div className="backup-stat-content">
              <h4>Database Size</h4>
              <p className="backup-stat-number">
                {backupStats.databaseSizeMB} MB
              </p>
            </div>
          </div>

          <div className="backup-stat-card">
            <div className="backup-stat-icon">
              <FaHistory />
            </div>
            <div className="backup-stat-content">
              <h4>Last Backup</h4>
              <p className="backup-stat-number">
                {backupStats.lastBackup
                  ? new Date(backupStats.lastBackup).toLocaleDateString()
                  : "Never"}
              </p>
            </div>
          </div>
        </div>

        {/* Backup Actions */}
        <div className="backup-actions">
          <div className="backup-primary-actions">
            <button
              className="backup-btn primary"
              onClick={createBackup}
              disabled={backupLoading}
            >
              <FaDatabase />
              {backupLoading ? "Creating Backup..." : "Create Full Backup"}
            </button>

            <button
              className="backup-btn secondary"
              onClick={downloadFullBackup}
            >
              <FaDownload />
              Download Full Backup (ZIP)
            </button>
          </div>

          <div className="backup-table-actions">
            <h4>Download Individual Tables:</h4>
            <div className="table-buttons">
              <button
                className="table-btn"
                onClick={() => downloadTableCSV("patients")}
                title={`${backupStats.recordCounts.patients || 0} records`}
              >
                <FaUser /> Patients ({backupStats.recordCounts.patients || 0})
              </button>
              <button
                className="table-btn"
                onClick={() => downloadTableCSV("appointments")}
                title={`${backupStats.recordCounts.appointments || 0} records`}
              >
                <FaCalendarAlt /> Appointments (
                {backupStats.recordCounts.appointments || 0})
              </button>
              <button
                className="table-btn"
                onClick={() => downloadTableCSV("contacts")}
                title={`${backupStats.recordCounts.contacts || 0} records`}
              >
                <FaUserMd /> Contacts ({backupStats.recordCounts.contacts || 0})
              </button>
              <button
                className="table-btn"
                onClick={() => downloadTableCSV("patient_notes")}
                title={`${backupStats.recordCounts.patient_notes || 0} records`}
              >
                <FaDatabase /> Notes (
                {backupStats.recordCounts.patient_notes || 0})
              </button>
              <button
                className="table-btn"
                onClick={() => downloadTableCSV("patient_reports")}
                title={`${
                  backupStats.recordCounts.patient_reports || 0
                } records`}
              >
                <FaFileDownload /> Reports (
                {backupStats.recordCounts.patient_reports || 0})
              </button>
            </div>
          </div>

          {/* Backup Message */}
          {backupMessage && (
            <div
              className={`backup-message ${
                backupMessage.includes("✅") ? "success" : "error"
              }`}
            >
              {backupMessage}
            </div>
          )}
        </div>

        {/* Backup Information */}
        <div className="backup-info">
          <h4>📋 Backup Information:</h4>
          <ul>
            <li>
              <strong>Full Backup:</strong> Creates a complete backup of all
              data in CSV format
            </li>
            <li>
              <strong>Individual Tables:</strong> Download specific data tables
              as CSV files
            </li>
            <li>
              <strong>ZIP Download:</strong> Downloads all tables in a
              compressed ZIP file
            </li>
            <li>
              <strong>Data Safety:</strong> Backups are stored on the server and
              can be shared with Dr. Satya
            </li>
            <li>
              <strong>Format:</strong> All data is exported in CSV format for
              easy viewing in Excel or Google Sheets
            </li>
          </ul>
        </div>
      </div>

      {/* Add New Patient Form Modal */}
      {showAddPatientForm && (
        <AddPatientForm
          onClose={() => setShowAddPatientForm(false)}
          onPatientAdded={() => {
            setShowAddPatientForm(false);
            fetchStats();
          }}
        />
      )}
    </motion.div>
  );
};

export default AdminDashboard;
